import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import EditDevice from './EditDevice';
import { API_BASE_URL } from '../config/api';
import { Device, User } from '../types';

interface DeviceDetailPageProps {
  user: User;
}

const DeviceDetailPage: React.FC<DeviceDetailPageProps> = ({ user }) => {
  const { serial } = useParams<{ serial: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [device, setDevice] = useState<Device | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const returnTo = searchParams.get('returnTo') || '/devices';
  const canDeleteDevices = ['admin', 'developer'].includes(user.role);
  const canEditDeploymentGroup = ['admin', 'developer'].includes(user.role);

  const fetchDevice = useCallback(async () => {
    if (!serial) return;

    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/devices/${encodeURIComponent(serial)}`, {
        withCredentials: true,
      });
      setDevice(response.data.device || response.data);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching device:', err);
      setError(err.response?.data?.message || 'Failed to fetch device');
    } finally {
      setLoading(false);
    }
  }, [serial]);

  useEffect(() => {
    fetchDevice();
  }, [fetchDevice]);

  const handleClose = () => {
    navigate(returnTo);
  };

  const handleSave = (updatedDevice: Device) => {
    setDevice(updatedDevice);
  };

  const handleDelete = () => {
    navigate(returnTo);
  };

  return (
    <div className="container-fluid mt-3 mb-5">
      <div className="mb-3">
        <Link to={returnTo} className="btn btn-sm btn-outline-secondary">
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back
        </Link>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '180px' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : device ? (
        <EditDevice
          device={device}
          onClose={handleClose}
          onSave={handleSave}
          onChange={setDevice}
          onDelete={canDeleteDevices ? handleDelete : undefined}
          canEditDeploymentGroup={canEditDeploymentGroup}
          variant="page"
        />
      ) : null}
    </div>
  );
};

export default DeviceDetailPage;
